import type { CustomSEO, SEOPageType } from '@iheartjane/data-access';
import { SEO_PAGE_TYPES } from '@iheartjane/data-access';
import {
  Banner,
  Box,
  ErrorIcon,
  Flex,
  Hide,
  Tabs,
  useToast,
} from '@iheartjane/reefer';
import isEmpty from 'lodash-es/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import type { BloomOperatorConfig } from '@jane/business-admin/types';
import { EventNames, businessPaths, track } from '@jane/business-admin/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';
import { SeoFormProvider } from './providers/SeoFormProvider';
import { NAV_LINKS } from './seoTabs/defaultSeoConstants';
import { PageSeo } from './seoTabs/PageSeo';

import styles from './PremiumSeo.module.css';

export const PremiumSeo = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [selectedPath, setSelectedPath] = useState<SEOPageType>('featured');
  const [isLoading, setIsLoading] = useState(false);

  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const resolvedSeo = bloomMenuQuery.data?.custom_seo;
  const seoDefaults = bloomMenuQuery.data?.custom_seo_defaults;

  const initialValues = useMemo<CustomSEO>(() => {
    const initialValues: CustomSEO = {};
    SEO_PAGE_TYPES.forEach((seoType) => {
      initialValues[seoType] = {
        title: resolvedSeo?.[seoType]?.title ?? '',
        description: resolvedSeo?.[seoType]?.description ?? '',
      };
    });
    return initialValues;
  }, [resolvedSeo]);

  const formMethods = useForm<CustomSEO>({
    defaultValues: initialValues,
    reValidateMode: 'onChange',
    mode: 'all',
  });
  const {
    control,
    formState: { errors },
    trigger,
  } = formMethods;
  const formValues = useWatch({ control });

  useEffect(() => {
    /**
     * Trigger validation when form values intially get set.
     * This handles the case where invalid SEO values were saved
     * before validation was implemented.
     */
    trigger();
  }, [JSON.stringify(formValues)]);

  const hasNonDefaultValues = useMemo(() => {
    const defaultValues = seoDefaults?.[selectedPath];
    return (
      formValues[selectedPath]?.title !== defaultValues?.title ||
      formValues[selectedPath]?.description !== defaultValues?.description
    );
  }, [formValues, selectedPath, seoDefaults]);

  const seoVariables = useMemo(() => {
    return {
      storeName: bloomMenuQuery.data?.stores[0]?.name,
      storeCity: bloomMenuQuery.data?.stores[0]?.city,
      storeStateAbbr: bloomMenuQuery.data?.stores[0]?.state,
      storeType: 'Pickup',
      storeFulfillment: bloomMenuQuery.data?.stores[0]?.recreational
        ? 'Recreational'
        : 'Medical',
      category: 'flower',
      menuUrl: bloomMenuQuery.data?.menu_url || '',
      specialName: 'Bogo great deal',
      rowName: 'row 1',
      productName: 'Green Chocolate',
      productLineage: 'Sativa',
      productCategory: 'Edible',
      productSubCategory: 'Chocolates',
      productBrand: "Jane's Bakery",
    };
  }, [bloomMenuQuery.data]);

  useEffect(() => {
    if (bloomMenuQuery.isSuccess && resolvedSeo) {
      formMethods.reset(initialValues);
    }
  }, [bloomMenuQuery.isSuccess, resolvedSeo, initialValues, formMethods.reset]);

  const publishSeoCustomization = (data: CustomSEO) => {
    setIsLoading(true);
    updateBloomMenu(
      {
        custom_seo: data,
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
          setIsLoading(false);
        },
        onSuccess: async () => {
          toast.add({
            label: 'SEO customization updated!',
            variant: 'success',
          });

          const bloomMenuData = bloomMenuQuery.data as BloomOperatorConfig;
          track({
            event: EventNames.EditedSeoInformation,
            operator_id: bloomMenuData.operator.id,
            operator_name: bloomMenuData.operator.name,
            old_content: bloomMenuData.custom_seo,
            new_content: data,
          });

          setIsLoading(false);
          navigate(businessPaths.premium());
        },
      }
    );
  };

  const resetTabOnly = (tabName: SEOPageType) => {
    formMethods.setValue(
      `${tabName}.title`,
      seoDefaults?.[tabName]?.title ?? '',
      {
        shouldDirty: true,
      }
    );
    formMethods.setValue(
      `${tabName}.description`,
      seoDefaults?.[tabName]?.description ?? '',
      { shouldDirty: true }
    );
  };

  const errorMessage = useMemo(() => {
    if (isEmpty(errors)) return undefined;
    const errorKeys = Object.keys(errors);
    const tabNames = errorKeys.map(
      (key) => NAV_LINKS.find((link) => link.value === key)?.label
    );
    return `Please fix the errors in ${tabNames.join(', ')} to publish.`;
  }, [JSON.stringify(errors)]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading}
      onSubmit={publishSeoCustomization}
      preview={{
        onClick: () => {
          resetTabOnly(selectedPath);
        },
        disable: isLoading || !hasNonDefaultValues,
        loading: isLoading,
        label: 'Restore default',
      }}
      title="SEO Customization"
    >
      <Flex height={'100%'}>
        <nav
          className={styles['sideNav']}
          aria-label="SEO customization navigation"
        >
          <Box minWidth="100px">
            <Tabs<SEOPageType>
              direction="vertical"
              onChange={setSelectedPath}
              tabWidth="150px"
              value={selectedPath}
            >
              {NAV_LINKS.map((item) => (
                <Tabs.Tab
                  value={item.value}
                  key={item.label}
                  label={
                    errors[item.value] ? (
                      <Flex alignItems="center">
                        <ErrorIcon mr={8} size="sm" color="error" />
                        {item.label}
                      </Flex>
                    ) : (
                      item.label
                    )
                  }
                  selected={item.value === selectedPath}
                />
              ))}
            </Tabs>
          </Box>
        </nav>

        <SeoFormProvider>
          <Flex className={styles['content']} flexDirection="column">
            {errorMessage && (
              <Banner
                variant="error"
                label={errorMessage}
                icon={<ErrorIcon />}
                mb={32}
              />
            )}

            <Hide isHidden={selectedPath !== 'featured'} hideFromScreenReader>
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}`}
                pageType="featured"
                pageDescription="the homepage"
                seoVariables={seoVariables}
              />
            </Hide>
            <Hide isHidden={selectedPath !== 'category'} hideFromScreenReader>
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/${seoVariables['category']}`}
                pageType="category"
                pageDescription="category pages"
                seoVariables={seoVariables}
              />
            </Hide>
            <Hide isHidden={selectedPath !== 'deal'} hideFromScreenReader>
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/deals/489/${
                  seoVariables['specialName']
                }`}
                pageType="deal"
                pageDescription="deal pages"
                seoVariables={seoVariables}
              />
            </Hide>
            <Hide isHidden={selectedPath !== 'shopAll'} hideFromScreenReader>
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/shop-all`}
                pageType="shopAll"
                pageDescription="shop all pages"
                seoVariables={{
                  ...seoVariables,
                  category: 'shop all',
                }}
              />
            </Hide>
            <Hide isHidden={selectedPath !== 'customRow'} hideFromScreenReader>
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/customRow/81917/${
                  seoVariables['rowName']
                }`}
                pageType="customRow"
                pageDescription="custom row pages"
                seoVariables={seoVariables}
              />
            </Hide>
            <Hide isHidden={selectedPath !== 'pdp'} hideFromScreenReader>
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/products/147331/foria-sativa-hawaiian-haze`}
                pageType="pdp"
                pageDescription="pdp pages"
                seoVariables={seoVariables}
              />
            </Hide>
          </Flex>
        </SeoFormProvider>
      </Flex>
    </ModalPageWrapper>
  );
};
